import { z } from "zod/v4";

import { E_Page } from "@lattice/frontend/lib/routes/routes";

import {
  WEB_SETTINGS_PANELS,
  type T_WebSettingsAccountUpdate,
  type T_WebSettingsPanel,
} from "./web-settings-contract";

const WEB_SETTINGS_INTENT_PANEL_PARAM = "settings";
const WEB_SETTINGS_INTENT_REFERRAL_PARAM = "referral";
const WEB_SETTINGS_INTENT_SUCCESS_PARAM = "show_success";

const z_WebSettingsPanel = z.enum(WEB_SETTINGS_PANELS);
const z_WebSettingsReferralCode = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/);
const z_WebSettingsAccountUpdate = z.enum(["email", "password"]);

export type T_WebSettingsIntent = {
  accountUpdate: T_WebSettingsAccountUpdate | null;
  panel: T_WebSettingsPanel;
  referralCode: string | null;
};

type T_SearchParamsReader = {
  getAll: (name: string) => string[];
  toString: () => string;
};

type T_CreateWebSettingsHrefOptions = {
  accountUpdate?: T_WebSettingsAccountUpdate;
  referralCode?: string;
};

export function createWebSettingsHref(
  panel: T_WebSettingsPanel,
  options: T_CreateWebSettingsHrefOptions = {},
): string {
  const params = new URLSearchParams({
    [WEB_SETTINGS_INTENT_PANEL_PARAM]: panel,
  });

  if (options.referralCode !== undefined) {
    if (panel !== "referrals") {
      throw new Error("Referral codes can only target the referrals panel.");
    }
    const referralResult = z_WebSettingsReferralCode.safeParse(
      options.referralCode,
    );
    if (referralResult.success) {
      params.set(WEB_SETTINGS_INTENT_REFERRAL_PARAM, referralResult.data);
    }
  }

  if (options.accountUpdate !== undefined) {
    if (panel !== "account") {
      throw new Error("Account updates can only target the account panel.");
    }
    params.set(WEB_SETTINGS_INTENT_SUCCESS_PARAM, options.accountUpdate);
  }

  return `${E_Page.Shell}?${params.toString()}`;
}

export function hasWebSettingsIntent(params: T_SearchParamsReader): boolean {
  return params.getAll(WEB_SETTINGS_INTENT_PANEL_PARAM).length > 0;
}

export function parseWebSettingsIntent(
  params: T_SearchParamsReader,
): T_WebSettingsIntent | null {
  const panelValues = params.getAll(WEB_SETTINGS_INTENT_PANEL_PARAM);
  if (panelValues.length !== 1) return null;

  const panelResult = z_WebSettingsPanel.safeParse(panelValues[0]);
  if (!panelResult.success) return null;

  const referralValues = params.getAll(WEB_SETTINGS_INTENT_REFERRAL_PARAM);
  const referralResult =
    panelResult.data === "referrals" && referralValues.length === 1
      ? z_WebSettingsReferralCode.safeParse(referralValues[0])
      : null;
  const successValues = params.getAll(WEB_SETTINGS_INTENT_SUCCESS_PARAM);
  const successResult =
    panelResult.data === "account" && successValues.length === 1
      ? z_WebSettingsAccountUpdate.safeParse(successValues[0])
      : null;

  return {
    accountUpdate: successResult?.success ? successResult.data : null,
    panel: panelResult.data,
    referralCode: referralResult?.success ? referralResult.data : null,
  };
}

export function removeWebSettingsIntentFromHref(
  pathname: string,
  params: T_SearchParamsReader,
): string {
  const remaining = new URLSearchParams(params.toString());
  remaining.delete(WEB_SETTINGS_INTENT_PANEL_PARAM);
  remaining.delete(WEB_SETTINGS_INTENT_REFERRAL_PARAM);
  remaining.delete(WEB_SETTINGS_INTENT_SUCCESS_PARAM);
  const query = remaining.toString();
  return query.length > 0 ? `${pathname}?${query}` : pathname;
}
